import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { isEmpty } from 'lodash-es';
import { BasicInput, EditTemplateIcon, MultiAutocomplete, MultiAutocompleteOptionProps, TertiaryButton, Typography, ViewTemplateIcon } from '@dev/ee-components';
import { getCheckoutCreatorStore, ModifyCheckout, ModifyEmailTemplate, SetCheckoutErrors, SetIsEmailTemplateOpen, SetTemplatePreview } from '@/store/checkoutCreator/actions';
import { TemplatePreviewButton } from '@/assets/TemplatePreviewButton';
import { CheckoutErrorNames } from '@/store/checkoutCreator/types';
import { getApiPath } from '@/services/Secuirty';
import { useHistoryBackActionContext } from '@/services/hooks/useHistoryBackAction';
import { HideDialog, ShowDialog } from '@/store/dialog/actions';
import { EmailDropdownWrapper, FieldGroupWrapper, ScreenshotHover, ScreenshotWrapper, Step5EditContentAudience, Step5EditContentAudienceInfo, Step5EditContentAudienceList, Step5EditContentColumns, Step5EditContentWrapper } from './styles';
import { EmailDropdown } from './components/EmailDropdown';
import { EmailSubject } from './components/EmailSubject';
import { useCreateListModalParams } from '../../hooks/useCreateListModalParams';
import { useGetDefaultSender } from '../../../../services/hooks/useGetDefaultSender';
import { useContactAudienceLists } from '../../hooks/useContactAudienceLists';

export const Step5EditContent = (): JSX.Element => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const contactLists = useContactAudienceLists();
    const historyBackAction = useHistoryBackActionContext();
    const { pushBackCallback, popBackCallback } = historyBackAction || {};
    const { getCreateListModalProps } = useCreateListModalParams({ popBackCallback });
    const { email: defaultEmail, name: defaultName } = useGetDefaultSender() ?? {};

    const { checkoutObject, checkoutPayload, postPurchaseTemplatePayload, errors } = useSelector(getCheckoutCreatorStore);
    
    const templateScreenshot = checkoutObject?.PostPurchaseTemplate?.Screenshot;

    const { FromName, FromEmail } = checkoutObject?.PostPurchaseTemplate || {};

    const { fromEmail: fromEmailPayload, fromName: fromNamePayload } = postPurchaseTemplatePayload || {};
    
    const viewTemplate = (): void => {
        const previewHtmlContent = checkoutObject?.PostPurchaseTemplate?.Body?.Parts?.find((part) => part.ContentType === 'text/html')?.Content;
        dispatch(SetTemplatePreview(previewHtmlContent));
    };

    const editTemplate = (): void => {
        dispatch(SetIsEmailTemplateOpen(true));
    };

    const changeFromNameHandle = (ev) => {
        setFromName(ev.currentTarget.value);
    };

    const setFromName = (value: string): void => {
        dispatch(ModifyEmailTemplate({ fromName: value }));
    };
    
    const setFromEmail = (value: string): void => {
        dispatch(ModifyEmailTemplate({ fromEmail: value }));

        if (!errors) {
            return;
        }
        dispatch(SetCheckoutErrors());
    };

    const selectedLists = checkoutPayload?.CheckoutAudience ?? checkoutObject?.CheckoutAudience ?? [];
    const autocompleteChosenOptions = contactLists?.filter((list) => selectedLists.includes(list.value as string)) ?? [];
    
    const getOptionsWithoutSelected = (options: MultiAutocompleteOptionProps[]): MultiAutocompleteOptionProps[] => {
        if (isEmpty(selectedLists)) {
            return options ?? [];
        }
        return options?.filter(item => !selectedLists.find(optionId => contactLists.find(list => list.value === optionId)?.label === item.label)) ?? [];
    };
    const closeModal = (): boolean => {
        dispatch(HideDialog());
        return true;
    };
    const handleCreateNewListClick = (): void => {
        dispatch(ShowDialog(getCreateListModalProps()));
        pushBackCallback(closeModal);
    };

    const handleOnAudienceChange = (selectedOptions: MultiAutocompleteOptionProps[]): void => {
        const chosenOptionsIds = selectedOptions?.map(option => option.value as string) ?? [];
        dispatch(ModifyCheckout({ CheckoutAudience: chosenOptionsIds }));
        if (!errors) {
            return;
        }
        dispatch(SetCheckoutErrors());
    };

    const isInvalidEmail = errors?.[CheckoutErrorNames.INVALID_FROM_EMAIL];
    const errorMessage = isInvalidEmail ? t('emailnotassociatederror') : t('thisfieldisrequired');

    return (
        <Step5EditContentWrapper>
            <Typography variant="body1">{t('postpurchasesettings_description')}</Typography>
            <Step5EditContentColumns>
                <div>
                <ScreenshotWrapper>
                    <ScreenshotHover>
                        <TemplatePreviewButton
                            id="checkout-edit-post-purchase-template"
                            buttonIcon={<EditTemplateIcon width={31.5} height={31.5} />}
                            buttonLabel={t('edit')}
                            handleOnClick={editTemplate}
                        />
                        <TemplatePreviewButton
                            id="checkout-view-post-purchase-template"
                            buttonIcon={<ViewTemplateIcon width={28.8} height={28.8} />}
                            buttonLabel={t('view')}
                            handleOnClick={viewTemplate}
                        />
                    </ScreenshotHover>
                    {templateScreenshot && <img src={`${getApiPath()}/userfile${encodeURI(templateScreenshot)}`} />}
                </ScreenshotWrapper>
                </div>
                <div>
                    <FieldGroupWrapper>
                        <div>
                            <BasicInput
                                id="checkout-post-purchase-from-name"
                                label={t('fromname')}
                                value={fromNamePayload ?? FromName ?? defaultName ?? ''}
                                onChange={changeFromNameHandle}
                            />
                        </div>
                        <EmailDropdownWrapper>
                            <EmailDropdown
                                setEmail={setFromEmail}
                                email={fromEmailPayload ?? (FromEmail || defaultEmail || '')} //it's on purpose, the backend changes null to an empty string
                                hasError={fromEmailPayload === '' || isInvalidEmail}
                                errorMessage={errorMessage}
                                id="checkout-post-purchase-from-email"
                                label={t('frommail')}
                                placeholder={t('enteravalidemailadress')}
                            />
                        </EmailDropdownWrapper>
                    </FieldGroupWrapper>
                    <FieldGroupWrapper>
                        <EmailSubject />
                    </FieldGroupWrapper>
                </div>
            </Step5EditContentColumns>

            <Step5EditContentAudience>
                <Step5EditContentAudienceInfo>
                    <Typography variant="h2">{t('checkoutaudience')}</Typography>
                    <Typography variant="body1">{t('checkoutaudience_description')}</Typography>
                </Step5EditContentAudienceInfo>
                <Step5EditContentAudienceList>
                    <MultiAutocomplete
                        id='select-list'
                        values={autocompleteChosenOptions}
                        options={getOptionsWithoutSelected(contactLists)}
                        onChange={handleOnAudienceChange}
                        noOptionsText={t('nolistsinfo')}
                        placeholder={t('searchorselectfromlist')}
                        errorMessage={t('selectoneormorelists')}
                        labelOnly
                        hideGroupLabel
                        error={errors?.[CheckoutErrorNames.EMPTY_CHECKOUT_AUDIENCE] || checkoutPayload?.CheckoutAudience?.length === 0}
                    />
                    <div>
                        <TertiaryButton text={t('createnewlist')} onClick={handleCreateNewListClick} />
                    </div>
                </Step5EditContentAudienceList>
            </Step5EditContentAudience>
        </Step5EditContentWrapper>
    );
};
